import React, { useState } from 'react';
import { Shield, Lock, ArrowRight, User } from 'lucide-react';

const AdminLogin = ({ onNavigate }) => {
    const [step, setStep] = useState(1); // 1: Credentials, 2: PIN
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        username: '',
        password: '',
        pin: ''
    });

    const handleCredentialsSubmit = (e) => {
        e.preventDefault();
        setLoading(true);

        // Simulate validation
        setTimeout(() => {
            setLoading(false);
            if (formData.username === 'admin' && formData.password === 'admin123') {
                setStep(2);
            } else {
                alert('Invalid Credentials');
            }
        }, 1000);
    };

    const handlePinSubmit = (e) => {
        e.preventDefault();
        setLoading(true);

        setTimeout(() => {
            setLoading(false);
            if (formData.pin === '1234') {
                onNavigate && onNavigate('admin');
            } else {
                alert('Invalid PIN');
            }
        }, 1000);
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-950 flex items-center justify-center px-4 py-20 relative overflow-hidden">
            {/* Ambient Background Effects */}
            <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
                <div className="absolute top-[-10%] left-[-10%] w-[500px] h-[500px] bg-emerald-500/20 rounded-full blur-[100px] animate-blob"></div>
                <div className="absolute bottom-[-10%] right-[-10%] w-[500px] h-[500px] bg-slate-500/20 rounded-full blur-[100px] animate-blob animation-delay-2000"></div>
                <div className="absolute top-[20%] right-[20%] w-[300px] h-[300px] bg-emerald-700/20 rounded-full blur-[80px] animate-blob animation-delay-4000"></div>
            </div>

            <div className="max-w-md w-full bg-white/10 backdrop-blur-xl rounded-3xl shadow-2xl overflow-hidden border border-white/10 relative z-10 animate-fade-in-up">

                {/* Header Visual */}
                <div className="bg-gradient-to-b from-white/5 to-transparent p-8 text-center relative overflow-hidden border-b border-white/5">
                    <div className="relative z-10 flex flex-col items-center">
                        <div className="w-20 h-20 bg-gradient-to-br from-emerald-400 to-teal-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg shadow-emerald-500/20 transform hover:scale-105 transition-transform duration-300">
                            <Shield className="w-10 h-10 text-white" />
                        </div>
                        <h2 className="text-3xl font-black text-white tracking-tight mb-2">Admin Portal</h2>
                        <div className="inline-flex items-center px-3 py-1 rounded-full bg-emerald-500/20 border border-emerald-500/30 text-emerald-300 text-xs font-bold uppercase tracking-wider">
                            <Lock className="w-3 h-3 mr-1.5" /> Secure Access
                        </div>
                    </div>
                </div>

                <div className="p-8">
                    {step === 1 ? (
                        <form onSubmit={handleCredentialsSubmit} className="space-y-6">
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2 ml-1">Username</label>
                                    <div className="relative group">
                                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                            <User className="h-5 w-5 text-slate-500 group-focus-within:text-emerald-400 transition-colors" />
                                        </div>
                                        <input
                                            type="text"
                                            required
                                            value={formData.username}
                                            onChange={e => setFormData({ ...formData, username: e.target.value })}
                                            className="block w-full pl-11 pr-4 py-4 bg-slate-900/50 border border-slate-700 rounded-xl text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50 transition-all font-medium"
                                            placeholder="Enter admin ID"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2 ml-1">Password</label>
                                    <div className="relative group">
                                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                            <Lock className="h-5 w-5 text-slate-500 group-focus-within:text-emerald-400 transition-colors" />
                                        </div>
                                        <input
                                            type="password"
                                            required
                                            value={formData.password}
                                            onChange={e => setFormData({ ...formData, password: e.target.value })}
                                            className="block w-full pl-11 pr-4 py-4 bg-slate-900/50 border border-slate-700 rounded-xl text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50 transition-all font-medium"
                                            placeholder="••••••••"
                                        />
                                    </div>
                                </div>
                            </div>

                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-white py-4 rounded-xl font-bold shadow-lg shadow-emerald-500/25 transition-all hover:scale-[1.02] active:scale-95 flex items-center justify-center group"
                            >
                                {loading ? 'Verifying...' : 'Continue'}
                                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                            </button>
                        </form>
                    ) : (
                        <form onSubmit={handlePinSubmit} className="space-y-8 animate-fade-in-up">
                            <div className="text-center">
                                <h3 className="text-xl font-bold text-white mb-2">Two-Factor Auth</h3>
                                <p className="text-slate-400 text-sm">Enter the 4-digit security PIN to verify identity.</p>
                            </div>

                            <div className="flex justify-center">
                                <input
                                    type="password"
                                    maxLength="4"
                                    required
                                    autoFocus
                                    value={formData.pin}
                                    onChange={e => setFormData({ ...formData, pin: e.target.value.replace(/[^0-9]/g, '') })}
                                    className="w-full max-w-[200px] text-center text-4xl font-bold tracking-[0.5em] bg-slate-900/50 border border-slate-700 rounded-xl py-4 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 text-white transition-all placeholder-slate-700"
                                    placeholder="••••"
                                />
                            </div>

                            <div className="space-y-4">
                                <button
                                    type="submit"
                                    disabled={loading || formData.pin.length !== 4}
                                    className="w-full bg-emerald-500 hover:bg-emerald-400 text-white py-4 rounded-xl font-bold shadow-lg shadow-emerald-500/25 transition-all hover:scale-[1.02] active:scale-95 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed group"
                                >
                                    {loading ? 'Authenticating...' : 'Access Dashboard'}
                                    <Shield className="w-5 h-5 ml-2 group-hover:scale-110 transition-transform" />
                                </button>

                                <button
                                    type="button"
                                    onClick={() => setStep(1)}
                                    className="w-full text-sm text-slate-500 hover:text-slate-300 font-medium transition-colors"
                                >
                                    Back to Credentials
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AdminLogin;
